import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { AppBar, Toolbar, Container, Box, Typography, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

// Import Google Fonts in the component to ensure they're available
// This would normally go in a global CSS file or index.html
const fontImportStyle = `
  @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700&family=Montserrat:wght@300;400;500;600&display=swap');
`;

// Header AppBar that changes on scroll - transparent initially, glass morphism when scrolled
const HeaderContainer = styled(AppBar)(({ theme, scrolled }) => ({
  position: 'fixed',
  zIndex: 1000,
  backgroundColor: scrolled ? 'rgba(70, 20, 20, 0.22)' : 'transparent',
  backdropFilter: scrolled ? 'blur(9.5px)' : 'none',
  WebkitBackdropFilter: scrolled ? 'blur(9.5px)' : 'none',
  boxShadow: scrolled ? '0 4px 30px rgba(0, 0, 0, 0.1)' : 'none',
  borderRadius: scrolled ? '16px' : '0',
  transition: 'all 0.3s ease',
  padding: scrolled ? '5px 0' : '15px 0',
  margin: scrolled ? '10px auto' : '0',
  maxWidth: scrolled ? 'calc(100% - 40px)' : '100%',
  left: scrolled ? '20px' : '0',
  right: scrolled ? '20px' : '0',
  width: scrolled ? 'auto' : '100%'
}));

// Using MUI Container directly

const HeaderWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%'
}));

const Logo = styled(Box)(({ theme }) => ({
  '& h1': {
    fontSize: '28px',
    margin: 0,
    fontFamily: '"Cinzel", serif',
    fontWeight: 700,
    letterSpacing: '1.5px',
    color: '#ffffff',
    textTransform: 'uppercase',
    textShadow: '0 2px 4px rgba(0,0,0,0.3)'
  },
  '& a': {
    textDecoration: 'none',
    color: 'inherit',
    transition: 'all 0.3s ease'
  },
  '& a:hover': {
    textShadow: '0 0 10px rgba(255,255,255,0.8)'
  }
}));

const Nav = styled(Box)(({ theme, isOpen, scrolled }) => ({
  display: 'flex',
  [theme.breakpoints.down('md')]: {
    display: isOpen ? 'block' : 'none',
    position: 'absolute',
    top: '100%',
    left: '0',
    right: '0',
    width: '90%',
    margin: '0 auto',
    backgroundColor: 'rgba(70, 20, 20, 0.22)',
    backdropFilter: 'blur(9.5px)',
    WebkitBackdropFilter: 'blur(9.5px)',
    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
    borderRadius: '16px',
    padding: '20px 0'
  }
}));

const NavList = styled(Box)(({ theme }) => ({
  display: 'flex',
  listStyle: 'none',
  margin: 0,
  padding: 0,
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    textAlign: 'center'
  }
}));

const NavItem = styled(Box)(({ theme }) => ({
  margin: '0 15px',
  [theme.breakpoints.down('md')]: {
    margin: '10px 0'
  }
}));

const NavLinkStyled = styled(NavLink)(({ theme, scrolled }) => ({
  color: '#ffffff',
  fontWeight: scrolled ? 500 : 600,
  textDecoration: 'none',
  position: 'relative',
  padding: '8px 0',
  textShadow: scrolled ? '0 1px 2px rgba(0,0,0,0.2)' : '0 2px 4px rgba(0,0,0,0.5)',
  '&:after': {
    content: '""',
    position: 'absolute',
    width: 0,
    height: '2px',
    background: '#ffffff',
    bottom: '-5px',
    left: 0,
    transition: 'width 0.3s ease',
    boxShadow: '0 0 5px rgba(255,255,255,0.5)'
  },
  '&:hover': {
    color: '#ffffff',
    textShadow: '0 0 8px rgba(255,255,255,0.8)'
  },
  '&:hover:after, &.active:after': {
    width: '100%'
  },
  '&.active': {
    fontWeight: 700
  }
}));

const MobileMenuToggle = styled(IconButton)(({ theme }) => ({
  display: 'none',
  color: '#ffffff',
  [theme.breakpoints.down('md')]: {
    display: 'block'
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)'
  }
}));

const Header = () => {
  // Add the font import to the document
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = fontImportStyle;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <HeaderContainer 
      scrolled={scrolled} 
      position="fixed" 
      elevation={0} 
      color="transparent"
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <HeaderWrapper>
            <Logo>
              <Link to="/">
                <Typography 
                  variant="h1" 
                  component="h1" 
                  sx={{ 
                    fontFamily: '"Cinzel", serif',
                    fontWeight: 700,
                    letterSpacing: '1.5px',
                    textTransform: 'uppercase',
                    textShadow: scrolled ? '0px 2px 4px rgba(0,0,0,0.3)' : '0px 2px 6px rgba(0,0,0,0.5)',
                    fontSize: { xs: '22px', sm: '26px', md: '28px' },
                    color: '#ffffff'
                  }}
                >
                  The Artisans Table
                </Typography>
              </Link>
            </Logo>
            <Nav isOpen={isMenuOpen} scrolled={scrolled} component="nav">
              <NavList component="ul">
                <NavItem component="li">
                  <NavLinkStyled to="/" end scrolled={scrolled}>Home</NavLinkStyled>
                </NavItem>
                <NavItem component="li">
                  <NavLinkStyled to="/menu" scrolled={scrolled}>Menu</NavLinkStyled>
                </NavItem>
                <NavItem component="li">
                  <NavLinkStyled to="/gallery" scrolled={scrolled}>Gallery</NavLinkStyled>
                </NavItem>
                <NavItem component="li">
                  <NavLinkStyled to="/about" scrolled={scrolled}>About Us</NavLinkStyled>
                </NavItem>
                <NavItem component="li">
                  <NavLinkStyled to="/contact" scrolled={scrolled}>Contact</NavLinkStyled>
                </NavItem>
              </NavList>
            </Nav>
            <MobileMenuToggle 
              onClick={toggleMenu}
              size="large"
              aria-label="menu"
            >
              {isMenuOpen ? <CloseIcon /> : <MenuIcon />}
            </MobileMenuToggle>
          </HeaderWrapper>
        </Toolbar>
      </Container>
    </HeaderContainer>
  );
};

export default Header;
